import React, { useContext, useEffect, useState } from 'react';
import styles from "./ServiceRequestDetails.module.css";
import { MdMiscellaneousServices } from "react-icons/md";
import AuthContext from '../../../../store/auth-context';
import { GlobalContext } from '../../../../store/global-context';
import { ServiceRequestContext } from '../../../../store/service-request';
import { Link, useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import CustomCard from '../../../Util/CustomCard';
import Comments from './Comments/Comments';
import RequestDetails from './RequestDetails/RequestDetails';
import UserDetails from './UserDetails/UserDetails';
import StageHistory from './StageHistory/StageHistory';
import { errorMessage } from "../../../Util/errorMessage";
import PageHeading from "../../../Util/PageHeading";
import { saveFile } from "../../../Util/download";


const ServiceRequestDetails = () => {
    const { requestId } = useParams();
    const navigate = useNavigate();
    const authCtx = useContext(AuthContext);
    const globalCtx = useContext(GlobalContext);
    const [currentStatus, setCurrentStatus] = useState('');
    const [selectedRequest, setSelectedRequest] = useState();
    const [isStageHistoryVisible, setIsStageHistoryVisible] = useState(false);
    const [downloading, setDownloading] = useState([]);

    // RCMC-08 (ticket 11): the page's one h1 names the request, "Service Request <ID>", while loading and after.
    // On screen it is still v1.2's "Service Requests / <ID>": the link back to the list sits beside the heading. The
    // name is one hidden text run and the shown ID is hidden from screen readers, so the name never depends on how a
    // browser joins a hidden prefix to the ID (review of ticket 11: a trailing space in a hidden span can be dropped).
    const heading = (
        <PageHeading icon={<MdMiscellaneousServices style={{marginRight:"5px"}} />} parent={<Link to=".." relative="route">Service Requests</Link>}>
            <span className="visually-hidden">{`Service Request ${requestId}`}</span>
            <span aria-hidden="true">{requestId}</span>
        </PageHeading>
    );

    useEffect(() => {
        axios
      .get("/api/user/service-request-details/"+requestId, {
        headers: { Authorization: "Bearer " + authCtx.authTokens.access },
      })
      .then((res) => {
        setSelectedRequest(res.data);
        setCurrentStatus(res.data.status);
      })
      .catch((e) => {
        globalCtx.raiseAlert(errorMessage(e), "error");
      });
    },[]);

    if(selectedRequest === undefined){
        return <div style={{ padding: "10px", maxHeight: "100vh", overflow: "auto" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems:"center",paddingBottom:"10px" }}>
            {heading}
            </div>
            <CustomCard styles={{padding:"20px",display:"flex",justifyContent:"center",alignItems:"center"}}>Loading...</CustomCard>
        </div>
    }

    // Which downloads are running (Q35 busy buttons): "" = the Supporting Document (ticket 18 — it had no busy state, so a
    // second press sent a second request), else a comment attachment's comment_id (ticket 22). Kept apart from the request
    // (review of 18): Operations' Stage update replaces the whole request with the server's copy, which wiped a busy state
    // kept on it, and the button was ready again mid-download.
    const markDownloading = (comment_id, busy) => setDownloading(prevState => busy ? [...prevState, comment_id] : prevState.filter((id) => id !== comment_id));
    const isDownloading = (comment_id) => downloading.includes(comment_id);

    // The Supporting Document's download (comment_id "") and each comment attachment's (RequestDetails, Comments)
    const downloadFile = (comment_id,doc_file,doc_file_name) => {
        // Its id (ticket 22): a failure takes this away, unless a newer confirmation has replaced it meanwhile
        const waiting = globalCtx.raiseAlert("Downloading file. Please wait...","success");
        markDownloading(comment_id, true);
        axios
        .get(`/api/operations/download-docs?doc_file=${doc_file}&doc_file_name=${doc_file_name}`, {
          headers: { Authorization: "Bearer " + authCtx.authTokens.access },responseType: 'blob'
        })
        .then((blob) => {
            // Q35 (ticket 21): nothing left on the page (v1.2 left an empty, unnamed link); shared since ticket 22
            saveFile(blob.data, doc_file_name);
            // Messages persist since ticket 03, so replace "Please wait" with the outcome
            globalCtx.raiseAlert(`File downloaded — ${doc_file_name}.`, "success");
            markDownloading(comment_id, false);
        })
        .catch((e) => {
            // Ticket 22 (Q35, found by its seam 2): v1.3 before 22 left "Please wait" beside the failure, as if still coming
            globalCtx.clearStatus(waiting);
            globalCtx.raiseAlert(errorMessage(e), "error");
            markDownloading(comment_id, false);
        });
    }

    const defaultStyle = {marginRight:"10px",display:"flex",flexDirection:"column",justifyContent:"space-around"};

    // RCMC-04 (ticket 16): the heading and Status History wrap onto two lines when the window is too narrow for one,
    // and below 768px the two columns (request details, comments) stack (ServiceRequestDetails.module.css)
    return (
        <>
            <div style={{ display: "flex", flexWrap: "wrap", gap: "10px", justifyContent: "space-between", alignItems:"center",padding:"10px" }}>
                {heading}
                <button type="button" onClick={() => {setIsStageHistoryVisible(true);}} style={{ background: "var(--portal-primary)", border:"none",boxShadow:"rgba(0, 0, 0, 0.24) 0px 3px 8px",borderRadius:"5px",color:"var(--portal-on-primary)", padding: "8px 20px",display:'flex',alignItems:"center"}}>
                    Status History
                </button>
                <StageHistory isStageHistoryVisible={isStageHistoryVisible} selectedRequest={selectedRequest} setIsStageHistoryVisible={setIsStageHistoryVisible} />
            </div>
            <div className={styles['columns']} style={{display:"flex",overflow:"hidden",padding:"0px 10px 10px 10px",flex:"1"}}>
                <div className={styles['details-column-left']} style={{...defaultStyle,...authCtx.user.is_admin? {width:"40%"}:{width:"30%"}}}>
                    <RequestDetails downloadFile={downloadFile} isDownloading={isDownloading} selectedRequest={selectedRequest} setSelectedRequest={setSelectedRequest} setCurrentStatus={setCurrentStatus} currentStatus={currentStatus} />
                    {authCtx.user.is_admin && <UserDetails selectedRequest={selectedRequest} />}
                </div>
                <Comments downloadFile={downloadFile} isDownloading={isDownloading} selectedRequest={selectedRequest} setSelectedRequest={setSelectedRequest} />
            </div>
        </>
    )
}

export default ServiceRequestDetails;
